/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { Draft, PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type {
  DataExplorerStoreType,
  DataExplorerPresets,
  SetSelectedDatasetPayload,
  SetQueryablesPayload,
  SetQueryableValuePayload,
  SetSearchFilterPayload,
  SetSelectedConceptPayload,
  SetWMSboxFilterPayload,
  SetBboxFilterPayload,
  SetMeteogateOnlyPayload,
} from './types';

export const initialState: DataExplorerStoreType = {
  filters: {
    searchFilter: '',
    queryables: [],
    concepts: [],
    queryableConfigurations: [],
    meteogateOnly: true,
  },
  selectedDatasetId: '',
  discoveryCatalogueURL: '',
  areaFilterTileURL: '',
  areaFilterTileLicense: '',
};

const slice = createSlice({
  initialState,
  name: 'dataexplorer',
  reducers: {
    setSearchFilter: (
      draft: Draft<DataExplorerStoreType>,
      action: PayloadAction<SetSearchFilterPayload>,
    ) => {
      const { filterText } = action.payload;
      draft.filters.searchFilter = filterText;
    },
    setQueryables: (
      draft: Draft<DataExplorerStoreType>,
      action: PayloadAction<SetQueryablesPayload>,
    ) => {
      const { queryables } = action.payload;
      draft.filters.queryables = queryables;
    },
    setQueryableValue: (
      draft: Draft<DataExplorerStoreType>,
      action: PayloadAction<SetQueryableValuePayload>,
    ) => {
      const { id, value, checked } = action.payload;
      const queryable = draft.filters.queryables.find(
        (queryable) => queryable.id === id,
      );
      if (queryable) {
        if (checked && !queryable.selected.includes(value)) {
          queryable.selected.push(value);
        }
        if (!checked && queryable.selected.includes(value)) {
          queryable.selected = queryable.selected.filter(
            (val) => val !== value,
          );
        }
      }
    },
    setSelectedDatasetId: (
      draft: Draft<DataExplorerStoreType>,
      action: PayloadAction<SetSelectedDatasetPayload>,
    ) => {
      const { id } = action.payload;
      draft.selectedDatasetId = id;
    },
    setSelectedConcept: (
      draft: Draft<DataExplorerStoreType>,
      action: PayloadAction<SetSelectedConceptPayload>,
    ) => {
      const { id } = action.payload;

      const isSelected = draft.filters.concepts.find(
        (concept) => concept.id === id,
      )!.selected;

      let newValue = !isSelected;

      // If All is clicked, always select it. Deselect all others
      if (id === 'all') {
        newValue = true;
        draft.filters.concepts = draft.filters.concepts.map((concept) => {
          return {
            id: concept.id,
            label: concept.label,
            selected: false,
          };
        });
      } else {
        draft.filters.concepts.find(
          (concept) => concept.id === 'all',
        )!.selected = false;
      }

      draft.filters.concepts.find((concept) => concept.id === id)!.selected =
        newValue;

      // If all concepts have been deselected, select All
      if (draft.filters.concepts.every((concept) => !concept.selected)) {
        draft.filters.concepts.find(
          (concept) => concept.id === 'all',
        )!.selected = true;
      }
    },
    setDataExplorerConfiguration: (
      draft: Draft<DataExplorerStoreType>,
      action: PayloadAction<DataExplorerPresets>,
    ) => {
      const {
        discoveryCatalogueURL,
        concepts,
        queryables,
        areaFilterTileURL,
        areaFilterTileLicense,
      } = action.payload;

      draft.discoveryCatalogueURL = discoveryCatalogueURL;
      draft.areaFilterTileURL = areaFilterTileURL;
      draft.areaFilterTileLicense = areaFilterTileLicense;

      draft.filters.concepts = [
        {
          id: 'all',
          label: 'All',
          selected: true,
        },
      ].concat(
        concepts.map((concept) => ({
          id: concept.id,
          label: concept.label,
          selected: false,
        })),
      );

      draft.filters.queryableConfigurations = queryables;
    },
    setWMSbox: (
      draft: Draft<DataExplorerStoreType>,
      action: PayloadAction<SetWMSboxFilterPayload>,
    ) => {
      const { wmsBox } = action.payload;
      draft.filters.wmsbox = wmsBox;
    },
    setBbox: (
      draft: Draft<DataExplorerStoreType>,
      action: PayloadAction<SetBboxFilterPayload>,
    ) => {
      const { bbox } = action.payload;
      draft.filters.bbox = bbox;
    },
    setMeteogateOnly: (
      draft: Draft<DataExplorerStoreType>,
      action: PayloadAction<SetMeteogateOnlyPayload>,
    ) => {
      const { enabled } = action.payload;
      draft.filters.meteogateOnly = enabled;
    },
  },
});

export const { reducer: dataExplorerReducer, actions: dataExplorerActions } =
  slice;
